/**
 * @fileoverview Main navigation component with responsive menu and smooth scroll integration.
 * Features auto-hiding navbar, accessibility support, and Lenis scroll integration.
 */

import { useState, useEffect } from 'react';
import MenuButton from './MenuButton';
import NavLink from './NavLink';
import { motion, AnimatePresence } from 'motion/react';
import { scrollToSection } from '../utils/lenisScroll';

/**
 * Navigation component providing main site navigation with smooth scrolling.
 *
 * @description Responsive navigation bar that auto-hides on scroll, includes mobile menu,
 * and uses Lenis for smooth scrolling to page sections. Features accessibility support
 * with skip links and proper ARIA labels.
 *
 * @returns {JSX.Element} The complete navigation component
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <>
 *       <Navigation />
 *       <main id="main-content">
 *         // Page content here
 *       </main>
 *     </>
 *   );
 * }
 * ```
 */
export default function Navigation() {
  /** Controls mobile menu open/closed state */
  const [menuOpen, setMenuOpen] = useState(false);

  /** Controls navbar visibility based on scroll direction */
  const [isVisible, setIsVisible] = useState(true);

  /** Tracks last scroll position for hide/show logic */
  const [lastScrollY, setLastScrollY] = useState(0);

  /** Desktop navigation text styling */
  const textStyle =
    'font-urbanist uppercase font-black text-lg tracking-tight text-white';

  /** Mobile navigation text styling with viewport-relative sizing */
  const lowBpTextStyle =
    'font-urbanist uppercase font-black text-[6vw] tracking-tight text-white';

  /**
   * Handles smooth scrolling to page sections using Lenis.
   *
   * @param {string} sectionId - The ID of the target section
   */
  const handleScrollToSection = (sectionId: string): void => {
    // -100px offset to account for fixed navbar height
    scrollToSection(sectionId, -100);
  };

  /**
   * Effect for managing navbar visibility based on scroll direction.
   * Hides navbar when scrolling down past 50px, shows when scrolling up.
   * Always keeps navbar visible when mobile menu is open.
   */
  useEffect(() => {
    const handleScroll = (): void => {
      const currentScrollY = window.scrollY;

      // Always keep navbar visible when mobile menu is open
      if (menuOpen) {
        setIsVisible(true);
        setLastScrollY(currentScrollY);
        return;
      }

      // Hide navbar when scrolling down past threshold (only when menu is closed)
      if (currentScrollY > lastScrollY && currentScrollY > 50) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    // Use passive listener for better performance
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, menuOpen]);

  return (
    <>
      {/* Main navigation bar */}
      <motion.nav
        initial={{ y: 0 }}
        animate={{ y: isVisible ? 0 : -100 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="fixed top-0 left-0 right-0 grid grid-cols-2 lg:grid-cols-3 items-center px-6 lg:px-9 py-3 w-screen z-50 h-auto bg-transparent mix-blend-difference"
        role="navigation"
        aria-label="Navegación principal"
      >
        {/* Logo */}
        <div className="justify-self-start">
          <a
            href="/"
            className="text-2xl font-bold hover:text-green-500 transition-colors"
          >
            <img
              src="/icons/logo.png"
              alt="Ghost Cvlt Logo"
              className="h-8 w-auto inline-block mr-2"
            />
          </a>
        </div>

        {/* Desktop navigation links */}
        <div className="hidden lg:flex justify-center space-x-4">
          <button
            onClick={() => handleScrollToSection('proyectos')}
            className={`${textStyle} bg-transparent border-none cursor-pointer`}
          >
            <NavLink href="#" text="Trabajo" className={textStyle} />
          </button>
          <button
            onClick={() => handleScrollToSection('servicios')}
            className={`${textStyle} bg-transparent border-none cursor-pointer`}
          >
            <NavLink href="#" text="Servicios" className={textStyle} />
          </button>
          <button
            onClick={() => handleScrollToSection('nosotros')}
            className={`${textStyle} bg-transparent border-none cursor-pointer`}
          >
            <NavLink href="#" text="Nosotros" className={textStyle} />
          </button>
        </div>

        {/* Desktop contact link */}
        <div className="hidden lg:block justify-self-end">
          <NavLink
            href="mailto:contacto@ghostcvlt.com"
            text="contacto@ghostcvlt.com"
            className={textStyle}
          />
        </div>

        {/* Mobile menu button */}
        <div className="lg:hidden justify-self-end">
          <MenuButton onClick={() => setMenuOpen(!menuOpen)} />
        </div>
      </motion.nav>

      {/* Mobile menu overlay */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: 'easeInOut' }}
            className="lg:hidden fixed top-0 left-0 right-0 bg-black h-screen z-40 overflow-hidden pointer-events-auto"
          >
            <div className="h-screen flex flex-col items-left">
              <div className="flex flex-col space-y-4 items-left pt-20 px-6">
                <button
                  onClick={() => {
                    handleScrollToSection('proyectos');
                    setMenuOpen(false);
                  }}
                  className={`${lowBpTextStyle} bg-transparent border-none cursor-pointer text-left`}
                >
                  <NavLink href="#" text="Trabajo" className={lowBpTextStyle} />
                </button>
                <button
                  onClick={() => {
                    handleScrollToSection('servicios');
                    setMenuOpen(false);
                  }}
                  className={`${lowBpTextStyle} bg-transparent border-none cursor-pointer text-left`}
                >
                  <NavLink
                    href="#"
                    text="Servicios"
                    className={lowBpTextStyle}
                  />
                </button>
                <button
                  onClick={() => {
                    handleScrollToSection('nosotros');
                    setMenuOpen(false);
                  }}
                  className={`${lowBpTextStyle} bg-transparent border-none cursor-pointer text-left`}
                >
                  <NavLink
                    href="#"
                    text="Nosotros"
                    className={lowBpTextStyle}
                  />
                </button>
                <NavLink
                  href="mailto:contacto@ghostcvlt.com"
                  text="contacto@ghostcvlt.com"
                  className={lowBpTextStyle}
                  onClick={() => setMenuOpen(false)}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
